<script lang="ts">
export default { name: 'DayUsagePanel' };
</script>

<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';

import { getDateKey } from './get-date-key';

import UsageTypeEmblem from './UsageTypeEmblem.vue';
import type {
  UsageDateMap,
  DayUsageInfo,
} from './LicenseUsageCalendarWidget.vue';

/**
 * `DayUsagePanel` component properties.
 */
export interface Props {
  /**
   * The date to display usage data for.
   */
  day: Date;
}

const props = defineProps<Props>();

const usageDateMap = inject('usageDateMap') as UsageDateMap;
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-arguments, @typescript-eslint/no-redundant-type-constituents
const selection = inject('selectedUsageInfo') as Ref<DayUsageInfo | null>;

const dateKey = computed(() => {
  return getDateKey(props.day);
});

const dataType = computed(() => {
  const data = usageDateMap.value[dateKey.value];

  if (!data) {
    return null;
  }

  /* eslint-disable @typescript-eslint/restrict-plus-operands */

  const totalUsedLicenses =
    data.usedStudentLicenses +
    data.usedInstructorLicenses +
    data.usedAdminLicenses;

  /* eslint-enable @typescript-eslint/restrict-plus-operands */

  if (totalUsedLicenses <= 0) {
    return null;
  }

  return data.type;
});

const selected = computed(() => {
  return dateKey.value && dateKey.value === selection.value?.key;
});

const classList = computed(() => {
  const items: string[] = [];

  if (selected.value) {
    items.push('selected');
  }

  if (dataType.value) {
    items.push(dataType.value);
  } else {
    items.push('no-usage');
  }

  return items;
});
</script>

<template>
  <div :id="dateKey" :class="['day-usage-panel', ...classList]">
    <div class="panel-content-top">
      <UsageTypeEmblem v-if="dataType" :type="dataType" :text="false" />
    </div>

    <div class="panel-content-bottom">
      <small v-if="!dataType" class="no-usage-text"> No usage data </small>
    </div>
  </div>
</template>

<style scoped lang="scss">
.day-usage-panel {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  font-size: 0.8rem !important;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &.no-usage {
    z-index: 1;
    pointer-events: none;

    @include app-theme-light {
      background-color: #e4e5ee;
    }

    @include app-theme-dark {
      background-color: #15161bd6;
    }
  }

  &.historical {
    color: var(--data-historical-color);
  }

  &.projected {
    color: var(--data-projected-color);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: transparent;
    z-index: -1;
    transition: background-color 0.2s;
  }

  &:hover::after {
    @include app-theme-light {
      background-color: #d4d8e6;
    }

    @include app-theme-dark {
      background-color: #30313d;
    }
  }

  &.selected::after {
    background-color: transparent !important;
    border: 2px solid var(--primary) !important;
  }
}

.panel-content-top {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 6px;
}

.panel-content-bottom {
  display: flex;
  justify-content: center;
  height: 20px;
  padding: 0 6px;
}

.no-usage-text {
  display: inline-block;
  opacity: 0.25;
  font-style: italic;
  line-height: 1.75em;
}
</style>
